import { useCallback, useEffect, useRef, useState } from 'react'
import { AppShell, type Route } from './components/AppShell.js'
import { Cockpit } from './pages/Cockpit.js'
import { Catalogue } from './pages/Catalogue.js'
import { Quotes } from './pages/Quotes.js'
import { Builder } from './pages/Builder.js'
import { AssetLibrary } from './pages/AssetLibrary.js'
import { Templates } from './pages/Templates.js'
import { Snippets } from './pages/Snippets.js'
import { CostLog } from './pages/CostLog.js'
import { SupplierQuotes } from './pages/SupplierQuotes.js'
import { Commissions } from './pages/Commissions.js'
import { Renewals } from './pages/Renewals.js'
import { ProductLines } from './pages/ProductLines.js'
import { Organisation } from './pages/Organisation.js'
import { TermsConditions } from './pages/TermsConditions.js'
import { ScheduledJobs } from './pages/ScheduledJobs.js'
import { Partners } from './pages/Partners.js'
import { AdminGate } from './components/AdminGate.js'
import { SignIn } from './components/SignIn.js'
import { Readiness } from './pages/Readiness.js'
import { BoardPack } from './pages/BoardPack.js'
import { XeroSettings } from './pages/XeroSettings.js'
import { Billing, BillingVariations } from './pages/Billing.js'
import { NeedsAttention } from './pages/NeedsAttention.js'
import {
  adminSignOut, adminStatus, fetchPortalContext, locateQuote, selectPartner,
  type AdminStatus, type PortalContext,
} from './lib/api.js'

/**
 * ⚠️ In live mode the active partner is SERVER-side state — the session's
 * actingAsPartnerId (decision 34). Switching partner must round-trip through
 * /api/act-as; treating it as a local dropdown value leaves the UI showing a
 * partner the server was never told about, and every partner-scoped request
 * then fails. That is exactly how the catalogue ended up returning 409.
 */

/** Decision 130: the switcher's "All partners — overview" sentinel (staff only). */
export const ALL_PARTNERS = '__all__'

export default function App() {
  const [ctx, setCtx] = useState<PortalContext | null>(null)
  const [status, setStatus] = useState<AdminStatus | null>(null)
  const [route, setRoute] = useState<Route>('cockpit')
  const [builderQuoteId, setBuilderQuoteId] = useState<string | null>(null)
  // Decision 125: the open template-editing session (workspace quote + template).
  const [tplEdit, setTplEdit] = useState<
    { templateId: string; quoteId: string; resumed: boolean } | null
  >(null)
  // Decision 112: cockpit tiles land on the lists PRE-FILTERED. Cleared when
  // the page is left so a manual visit starts unfiltered.
  const [quotesInit, setQuotesInit] = useState<{ status?: string; owner?: string; stage?: string } | null>(null)   // #201: stage from the pipeline tile
  const [renewalsInit, setRenewalsInit] = useState<{ tile?: string; owner?: string; openId?: string } | null>(null)
  const [switching, setSwitching] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // Decision 130: the staff-only all-partners oversight view. PURELY client
  // state — the server session's acting-as is untouched (decision 34), which
  // is why leaving the view lands back on the acting partner unchanged.
  const [allView, setAllView] = useState(false)

  const load = useCallback(() => {
    adminStatus().then(setStatus)
    fetchPortalContext().then(setCtx).catch((e: Error) => setError(e.message))
  }, [])
  useEffect(load, [load])

  // Deep link from a HubSpot deal's wave9_quote_url (decision 84): ?quote=<id>
  // opens the builder once a partner context exists.
  //
  // Decision 187: it now SWITCHES ACTING-AS first. The builder's load is
  // partner-scoped, so a link to a quote belonging to any partner other than
  // the session's current one used to 404 — which is most links, since nothing
  // about arriving from HubSpot implies you were already acting as its partner.
  // Same switch-then-open shape as openFromAllView and openRenewalQuote below.
  // ⚠️ Guarded by a ref: the effect re-runs when ctx changes, and selectPartner
  // changes ctx, so without it the deep link would chase its own tail.
  // Decision 213: the Xero OAuth callback lands on /?xero=connected|error —
  // open the Xero admin page with the outcome, once, and clean the URL.
  const [xeroOutcome, setXeroOutcome] = useState<{ kind: 'connected' | 'error'; reason: string } | null>(null)
  const xeroLanded = useRef(false)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const outcome = params.get('xero')
    if (!outcome || !ctx?.isStaff || xeroLanded.current) return
    xeroLanded.current = true
    window.history.replaceState(null, '', '/')
    setXeroOutcome({ kind: outcome === 'connected' ? 'connected' : 'error', reason: params.get('reason') ?? '' })
    setRoute('xero')
  }, [ctx?.isStaff])

  // Decision 223: the HubSpot deal's "W9 Billing (portal)" link — ?billing=<dealId>
  // opens the review screen (AdminGate still decides who sees it).
  const [billingDeal, setBillingDeal] = useState<string | null>(null)
  const billingLinked = useRef(false)
  useEffect(() => {
    const id = new URLSearchParams(window.location.search).get('billing')
    if (!id || !/^\d{1,20}$/.test(id) || !ctx?.isStaff || billingLinked.current) return
    billingLinked.current = true
    window.history.replaceState(null, '', '/')
    setBillingDeal(id)
    setRoute('billing')
  }, [ctx?.isStaff])

  const deepLinked = useRef(false)
  useEffect(() => {
    const id = new URLSearchParams(window.location.search).get('quote')
    // Decision 190 (L6): only a uuid ever goes into the path — anything else
    // in ?quote= is junk, not a route to try.
    if (!id || !/^[0-9a-f-]{36}$/i.test(id) || !ctx?.isStaff || deepLinked.current) return
    deepLinked.current = true
    window.history.replaceState(null, '', '/')
    void (async () => {
      try {
        const found = await locateQuote(id)
        if (found.partnerId !== ctx.activePartnerId) {
          await selectPartner(found.partnerId)
          setCtx(await fetchPortalContext())
        }
      } catch (e) {
        // Not fatal: open it anyway. If the quote really is unreachable the
        // builder says so, which beats swallowing the link silently.
        setError((e as Error).message)
      }
      setAllView(false)
      setBuilderQuoteId(id)
      setRoute('builder')
    })()
  }, [ctx?.isStaff, ctx?.activePartnerId, ctx])

  const signOut = async () => {
    await adminSignOut().catch(() => {})
    setRoute('cockpit')
    load()
  }

  const choosePartner = async (id: string) => {
    // Decision 130: "All partners — overview" is a VIEW, not an act-as — no
    // server round-trip; picking a real partner steps back out of it.
    if (id === ALL_PARTNERS) {
      setAllView(true)
      setQuotesInit(null)
      setRoute('quotes')
      return
    }
    setAllView(false)
    setSwitching(true)
    setError(null)
    try {
      await selectPartner(id)
      setCtx(await fetchPortalContext())
      // A quote belongs to ONE partner — never leave the builder open across a switch.
      if (route === 'builder') { setBuilderQuoteId(null); setRoute('quotes') }
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setSwitching(false)
    }
  }

  const openBuilder = (quoteId: string) => {
    setBuilderQuoteId(quoteId)
    setRoute('builder')
  }

  // Decision 130: opening a quote from the all-partners view — switch the
  // session's acting-as to ITS partner first (decision 34, server-side), then
  // open. Mirrors openRenewalQuote below, which set the precedent (#105).
  const openFromAllView = async (quoteId: string, partnerId: string) => {
    if (partnerId !== ctx?.activePartnerId) {
      try {
        await selectPartner(partnerId)
        setCtx(await fetchPortalContext())
      } catch (e) {
        setError((e as Error).message)
        return
      }
    }
    setAllView(false)
    openBuilder(quoteId)
  }

  // Decision 112: tile navigation from the cockpit.
  const goQuotes = (filter?: { status?: string; owner?: string; stage?: string }) => {
    setQuotesInit(filter ?? null)
    setRoute('quotes')
  }
  const goRenewals = (filter?: { tile?: string; owner?: string; openId?: string }) => {
    setRenewalsInit(filter ?? null)
    setRoute('renewals')
  }

  // Decision 105: renewal quotes live on the internal partner — opening one
  // from the Renewals page switches "Acting as" there first (server-side
  // state, decision 34), or the builder's partner-scoped load would 404.
  const openRenewalQuote = async (quoteId: string) => {
    const internal = ctx?.partners.find((p) => p.mode === 'internal')
    if (internal && internal.id !== ctx?.activePartnerId) {
      try {
        await selectPartner(internal.id)
        setCtx(await fetchPortalContext())
      } catch (e) {
        setError((e as Error).message)
        return
      }
    }
    openBuilder(quoteId)
  }

  if (error && !ctx) return <p style={{ padding: 24 }} className="pill pill--stop">{error}</p>
  if (!ctx || !status) return <p style={{ padding: 24 }} className="muted">Loading…</p>

  // Nothing in the portal is public. Sign in first, then the shell renders.
  if (!ctx.isStaff) return <SignIn status={status} onDone={load} />

  const partner = ctx.partners.find((p) => p.id === ctx.activePartnerId) ?? null
  const noPartner = (
    <NoPartner partners={ctx.partners.length} admin={ctx.role === 'admin'} onGo={() => setRoute('partners')} />
  )

  return (
    <AppShell
      route={route}
      // Decision 130: any nav click leaves the all-partners view — the pages it
      // lands on are acting-partner-scoped, and the switcher must say so.
      onRoute={(r) => { setAllView(false); setQuotesInit(null); setRenewalsInit(null); setRoute(r) }}
      role={ctx.role}
      onSignOut={signOut}
      partner={partner}
      partners={ctx.partners}
      onPartner={choosePartner}
      allView={allView}
      switching={switching}
      error={error}
    >
      {route === 'cockpit' && (partner
        ? <Cockpit key={partner.id} partner={partner} role={ctx.role} onOpen={openBuilder}
            onGoQuotes={goQuotes} onGoRenewals={goRenewals} onGoCommissions={() => setRoute('commissions')} onGoAttention={() => setRoute('attention')} />
        : noPartner)}
      {/* Decision 209: the Needs-attention page (was a cockpit panel). */}
      {route === 'attention' && (partner ? <NeedsAttention key={partner.id} partner={partner} onOpen={openBuilder} /> : noPartner)}
      {route === 'catalogue' && (partner ? <Catalogue key={partner.id} partner={partner} /> : noPartner)}
      {/* Decision 130: the all-partners oversight view rides the same page. */}
      {route === 'quotes' && (allView
        ? <Quotes key="all-partners" partner={partner} allPartners onOpen={openBuilder}
            onOpenPartner={(qid, pid) => { void openFromAllView(qid, pid) }} staff={ctx.role !== null} admin={ctx.role === 'admin'} />
        : partner
          ? <Quotes key={`${partner.id}:${JSON.stringify(quotesInit)}`} partner={partner} onOpen={openBuilder}
              initialStatus={quotesInit?.status} initialOwner={quotesInit?.owner} initialStage={quotesInit?.stage} staff={ctx.role !== null} admin={ctx.role === 'admin'} />
          : noPartner)}
      {route === 'builder' && (partner && builderQuoteId
        ? <Builder key={builderQuoteId} partner={partner} quoteId={builderQuoteId} role={ctx.role} onBack={() => setRoute('quotes')} onOpen={openBuilder} />
        : noPartner)}
      {/* Decision 125: the same builder over a template WORKSPACE row — no
          customer, deal or send surface, and it saves back to the template. */}
      {route === 'templateEditor' && (partner && tplEdit
        ? <Builder
            key={tplEdit.quoteId} partner={partner} quoteId={tplEdit.quoteId} role={ctx.role}
            onBack={() => { setTplEdit(null); setRoute('templates') }}
            templateMode={{
              templateId: tplEdit.templateId,
              resumed: tplEdit.resumed,
              onSaved: () => { setTplEdit(null); setRoute('templates') },
              onDiscarded: () => { setTplEdit(null); setRoute('templates') },
            }}
          />
        : noPartner)}
      {/* Decision 105: staff-only nav; the API refuses partner sessions too. */}
      {route === 'renewals' && (
        <Renewals key={JSON.stringify(renewalsInit)} onOpen={(id) => { void openRenewalQuote(id) }}
          initialTile={renewalsInit?.tile} initialOwner={renewalsInit?.owner} initialOpenId={renewalsInit?.openId} />
      )}
      {/* Decision 111: snippets + tokens for everyone — permissions unchanged, just surfaced. */}
      {route === 'snippets' && (partner ? <Snippets key={partner.id} partner={partner} role={ctx.role} /> : noPartner)}
      {/* Decision 93: partner-independent and open to every Wave9 internal user — no AdminGate, no partner needed. */}
      {/* Decision 135: reseller sessions manage their own library here too. */}
      {route === 'library' && <AssetLibrary reseller={ctx.role === null && partner?.mode === 'reseller'} />}
      {/* Decision 96: browsable without a partner; starting a quote needs one. */}
      {route === 'templates' && (
        <Templates partner={partner} role={ctx.role} onOpen={openBuilder}
          onEdit={(templateId, quoteId, resumed) => {
            setTplEdit({ templateId, quoteId, resumed })
            setRoute('templateEditor')
          }} />
      )}
      {/* Decision 97: staff-only nav; the API refuses partner sessions too. */}
      {route === 'costlog' && <CostLog partner={partner} onOpen={openBuilder} />}
      {/* Decision 211: the distributor quotes behind one-off Meraki sales. */}
      {route === 'supplierQuotes' && <SupplierQuotes partner={partner} onOpen={openBuilder} />}
      {/* Decision 183: one page, two audiences — an agent's own statements, or
          every agent's for a Wave9 admin (who also owns paid/reopen). */}
      {route === 'commissions' && (
        <Commissions partner={partner} admin={ctx.role === 'admin'} onOpen={openBuilder} />
      )}
      {/* Decision 205: Admin section (Mark); company-wide, no partner needed. */}
      {route === 'boardPack' && <AdminGate role={ctx.role}><BoardPack admin={ctx.role === 'admin'} /></AdminGate>}
      {/* Decision 213: the Xero connection lives with the admin pages. */}
      {route === 'xero' && <AdminGate role={ctx.role}><XeroSettings outcome={xeroOutcome} /></AdminGate>}
      {/* Decision 223: staff only — the nav hides it from partners; the API refuses them. */}
      {route === 'billing' && <AdminGate role={ctx.role}><Billing initialDealId={billingDeal} /></AdminGate>}
      {route === 'billingVariations' && <AdminGate role={ctx.role}><BillingVariations onOpenDeal={(id) => { setBillingDeal(id); setRoute('billing') }} /></AdminGate>}
      {route === 'partners' && <AdminGate role={ctx.role}><Partners /></AdminGate>}
      {route === 'productLines' && <AdminGate role={ctx.role}><ProductLines /></AdminGate>}
      {route === 'organisation' && <AdminGate role={ctx.role}><Organisation /></AdminGate>}
      {/* Decision 135: the T&Cs library on its own Admin page. */}
      {route === 'terms' && <AdminGate role={ctx.role}><TermsConditions /></AdminGate>}
      {route === 'jobs' && <AdminGate role={ctx.role}><ScheduledJobs /></AdminGate>}
      {route === 'readiness' && <AdminGate role={ctx.role}><Readiness /></AdminGate>}
    </AppShell>
  )
}

/**
 * An admin session isn't scoped to a partner until one is chosen, and a fresh
 * database has none to choose. Say which of those it is rather than rendering an
 * empty page and leaving the reason to be guessed.
 */
function NoPartner({ partners, admin, onGo }: { partners: number; admin: boolean; onGo: () => void }) {
  return (
    <div className="page-head">
      <div>
        <h1>{partners === 0 ? 'No partners yet' : 'No partner selected'}</h1>
        <p>
          {partners === 0
            ? admin
              ? 'The catalogue sync fills the product cache; partners are a separate thing and none exist yet. Nothing on these pages works until there is at least one.'
              : 'No partners exist yet, and creating one needs the admin key. Nothing on these pages works until there is at least one.'
            : 'A Wave9 session is not scoped to a partner. Choose one under "Acting as" at the bottom of the navigation to see their view.'}
        </p>
      </div>
      {partners === 0 && admin && <button className="btn btn--primary" onClick={onGo}>Go to Partners</button>}
    </div>
  )
}
